import { RemoteConfigModel } from 'src/models/remoteConfig.models'
import type { CapabilityDecisionResponse } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import {
  isCardManagementActionAvailable,
  isCardManagementViewAvailable,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'

/**
 * Check if SSO Login feature is enabled for the user.
 * Returns true if either:
 * 1. The global enable_sso_login flag is enabled, OR
 * 2. The user's organization ID is in the enable_sso_login_whitelist
 *
 * Note: On the sign-in page, organizationId may not be available yet.
 * In that case, only the global flag is checked.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional for sign-in page)
 * @returns true if SSO Login is enabled for this user
 */
export const isSSOLoginEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { enable_sso_login, enable_sso_login_whitelist } = remoteConfig

  // If org ID is available, check the whitelist
  if (organizationId) {
    return (
      enable_sso_login || enable_sso_login_whitelist.includes(organizationId)
    )
  }

  // On sign-in page (no org ID), only use the global flag
  return enable_sso_login
}

/**
 * Check if AI Insights feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_ai_insights flag is enabled, OR
 * 2. The user's organization ID is in the ai_insights_allowlist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if AI Insights is enabled for this user
 */
export const isAIInsightsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_ai_insights, ai_insights_allowlist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_ai_insights || ai_insights_allowlist.includes(organizationId)
}

/**
 * Check if Extend AI Agent feature is enabled for the user.
 * Returns true if either:
 * 1. The global show_extend_ai_agent flag is enabled, OR
 * 2. The user's organization ID is in the extendAIWhitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if Extend AI Agent is enabled for this user
 */
export const isExtendAIEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const { show_extend_ai_agent, extendAIWhitelist } = remoteConfig

  // Feature is enabled if the global flag is on OR the org is in the allowlist
  return show_extend_ai_agent || extendAIWhitelist.includes(organizationId)
}

/**
 * Check if the spend reporting dashboard is enabled for the user.
 * Returns true if either:
 * 1. The global enable_spend_reporting_dashboard flag is enabled, OR
 * 2. The user's organization ID is in the
 *    enable_spend_reporting_dashboard_whitelist
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @returns true if the spend reporting dashboard is enabled for this user
 */
export const isSpendReportingDashboardEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  const {
    enable_spend_reporting_dashboard,
    enable_spend_reporting_dashboard_whitelist,
  } = remoteConfig

  return (
    enable_spend_reporting_dashboard ||
    enable_spend_reporting_dashboard_whitelist.includes(organizationId)
  )
}

/** Show transaction comments when the client feature flag is enabled. */
export const isTransactionConversationsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.enable_transaction_conversations
}

/** Use save-on-blur for transaction free-text expense categories when enabled. */
export const isTransactionFreeTextSaveOnBlurEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => remoteConfig?.enable_transaction_free_text_save_on_blur === true

export const isExpensePolicyAIEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { show_expense_policy_ai, show_expense_policy_ai_whitelist } =
    remoteConfig

  if (organizationId) {
    return (
      show_expense_policy_ai ||
      show_expense_policy_ai_whitelist.includes(organizationId)
    )
  }

  return show_expense_policy_ai
}

export const isExpensePolicyAIChatEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.show_expense_policy_ai_chat
}

export const isGoogleWorkspaceIntegrationsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  return remoteConfig?.show_google_workspace_integrations === true
}

/**
 * Check if the redesigned physical card Account Settings tiles are enabled.
 * Gates the new bento layout (Card Status & Lifecycle, Spend Limit, etc.) on
 * the physical card details page. EX-40354.
 *
 * Client flag OR the server's demo-enrollment verdict
 * (`VIEW_PHYSICAL_CARD_MANAGEMENT`), matching the hero and the Statements /
 * Activity tabs (EX-40599). The bento is the surface those tiles' per-action
 * capabilities describe, so an org the server has enrolled is entitled to it
 * without a flag rollout; the flag stays a unilateral override for everyone
 * else.
 *
 * BOTH CALLERS MUST PASS THE SAME CAPABILITIES. This gate is read twice — by
 * `AccountEntityPage` to rename the tab "Controls & Settings", and by
 * `AccountSettings` to pick the bento over the legacy boxes. They must agree, or
 * a user lands on a tab whose name promises a surface it does not render. Both
 * read the same `capabilities(creditCardId)` cache entry, so they do.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the updated physical card settings UI is enabled
 */
export const isUpdatedPhysicalCardSettingsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_physical_card_settings === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Check if the self-service confirm-card-details flow is enabled. EX-40641.
 *
 * Gates only the missing-details entry points (panel/row visibility for
 * status=null cards and routing into ConfirmCardDetailsModal); the standard
 * connect flow for cards with complete details is unaffected.
 *
 * @param remoteConfig - the remote config model
 * @returns true if the confirm-card-details flow is enabled
 */
export const isCardDetailsConfirmationEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => remoteConfig?.card_details_confirmation === true

/**
 * Check if the card-detail Statements tab is enabled. EX-40600.
 *
 * Client flag OR the server's `READ_PHYSICAL_CARD_STATEMENTS` capability, so an
 * enrolled org sees the tab with the flag off and the flag stays a unilateral
 * override for everyone else. The capability half landed with the backend's
 * view-capability slice; before that the contract had no statements action at
 * all and this gate was flag-only.
 *
 * Enrollment and servable limits are separate preconditions on the server — an
 * enrolled non-USD card has its limit actions UNAVAILABLE while the read-only
 * view actions stay AVAILABLE — so this must key on its own action and never
 * treat another action's verdict as a proxy.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the Statements tab should render
 */
export const isUpdatedCardStatementsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_card_statements === true ||
  isCardManagementActionAvailable(capabilities, 'READ_PHYSICAL_CARD_STATEMENTS')

/**
 * Check if the card-detail Activity tab is enabled. EX-40600.
 *
 * Same shape as {@link isUpdatedCardStatementsEnabled}, keyed on
 * `READ_PHYSICAL_CARD_ACTIVITY`. The two tabs stay independent — separate flags
 * and separate actions — so they can ship separately and so an org enrolled for
 * one is not handed the other.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view
 * @returns true if the Activity tab should render
 */
export const isUpdatedCardActivityEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_card_activity === true ||
  isCardManagementActionAvailable(capabilities, 'READ_PHYSICAL_CARD_ACTIVITY')

/**
 * Whether the card detail header renders at all. EX-40350/40351/40352.
 *
 * One gate for the whole hero. It *replaces* the legacy AccountHeader and every
 * region (summary, card display, actions menu) renders together, so there is no
 * per-region visibility gate — on shows all of it, off shows the legacy header.
 *
 * As of EX-40596 it is also the only gate on what the hero *reads*. There is no
 * second flag selecting demo or live: every region takes the live path whenever
 * the hero renders, and each one's own capability decides whether it can serve a
 * figure. A region with no verdict renders unavailable rather than a stand-in,
 * so nothing behind this gate is fabricated.
 *
 * Client flag OR the server's demo-enrollment verdict, the same shape as
 * {@link isUpdatedCardStatementsEnabled} and {@link isUpdatedCardActivityEnabled}
 * (EX-40599). This gate was flag-only until now for the reason recorded on
 * {@link isUpdatedCardOverviewEnabled} — "no action means *may view the
 * redesigned surface*" — which is no longer true:
 * `VIEW_PHYSICAL_CARD_MANAGEMENT` is exactly that verdict, and it is what the
 * API already uses to decide whether this card is in the demo at all. ORing it
 * in means an enrolled org sees the hero without a flag rollout, while the flag
 * stays a unilateral override for everyone else.
 *
 * Keyed on the VIEW action alone and never on another action's verdict: the
 * server decides enrollment and per-action servability separately, so an
 * enrolled card can have, say, its limit actions UNAVAILABLE while it is still
 * entitled to the surface.
 *
 * TIMING: the capability arrives asynchronously and fails closed, so an enrolled
 * org with the flag off renders the legacy header for as long as the
 * capabilities query is in flight, then swaps. That is a visible swap, not a
 * correctness problem — and it is why the flag remains the way to get a
 * flicker-free rollout.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the card detail header should replace the legacy header
 */
export const isUpdatedCardSummaryEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_card_summary === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Check if the redesigned budget table is enabled. EX-40737.
 *
 * Governs BOTH budget tables on the card-detail page — the Overview tab's
 * snapshot and the Budgets tab's full list — as one gate, because they are the
 * same table under two headings. Splitting them would let a user read a budget's
 * spend against its limit on Overview and then land on a Budgets tab that
 * reports exposure and remaining instead, for the same rows.
 *
 * Client flag OR `VIEW_PHYSICAL_CARD_MANAGEMENT`, the server's demo-enrollment
 * verdict — the same shape as {@link isUpdatedCardSummaryEnabled} and
 * {@link isUpdatedPhysicalCardSettingsEnabled}. Keyed on the VIEW action rather
 * than a read action of its own: the budget rows come from the ordinary
 * `/creditcards` list, not from Card Management, so no `CardManagementAction`
 * describes them. What enrollment answers here is "is this card on the
 * redesigned surface", which is exactly what the VIEW verdict means.
 *
 * Unlike {@link isUpdatedCardOverviewEnabled} this is a pure CONTENT swap that
 * never touches the tab set: both tabs exist either way, and off renders the
 * legacy `BudgetsBox` untouched.
 *
 * TIMING: the capability arrives asynchronously and fails closed, so an enrolled
 * org with the flag off renders the legacy table until the capabilities query
 * settles, then swaps. Visible, not a correctness problem — and the reason the
 * flag remains the way to get a flicker-free rollout.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the redesigned budget table should render
 */
export const isUpdatedBudgetsTableEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_budgets_table === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Check if the redesigned virtual-card table is enabled. EX-40736.
 *
 * Governs the Virtual Cards TAB only — the table it renders. Deliberately NOT
 * the Overview snapshot, which is the one asymmetry with
 * {@link isUpdatedBudgetsTableEnabled}: that snapshot already shipped under
 * {@link isUpdatedCardOverviewEnabled}, so gating it again here would let this
 * flag switch off a table the Overview gate says should render.
 *
 * The two tables cannot disagree about a card the way the budget tables could,
 * because they render the same component from the same query — only the heading
 * slot and the paging props differ.
 *
 * Client flag OR `VIEW_PHYSICAL_CARD_MANAGEMENT`, the server's demo-enrollment
 * verdict, keyed on the VIEW action for the same reason
 * {@link isUpdatedBudgetsTableEnabled} is: virtual cards come from the ordinary
 * `/virtualcards` list, not from Card Management, so no `CardManagementAction`
 * describes them, and what enrollment answers here is "is this card on the
 * redesigned surface".
 *
 * A pure CONTENT swap that never touches the tab set: the Virtual Cards tab
 * exists either way, and off renders the legacy `VirtualCardsBoxAndLinks`
 * untouched.
 *
 * TIMING: the capability arrives asynchronously and fails closed, so an enrolled
 * org with the flag off renders the legacy table until the capabilities query
 * settles, then swaps. Visible, not a correctness problem — and the reason the
 * flag remains the way to get a flicker-free rollout.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the redesigned virtual-card table should render
 */
export const isUpdatedVirtualCardsTableEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_virtual_cards_table === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Check if the redesigned transaction-table treatment is enabled. EX-40736.
 *
 * The narrowest gate on this page: PRESENTATION only. It selects the shared
 * grid's header/row treatment on the card-detail Transactions tab and changes
 * nothing about what is fetched or shown — the filter schema, saved views,
 * column config, expense categories, receipt/sync columns and bulk actions are
 * identical either side of it, so off and on serve the same rows.
 *
 * That is why the Transactions tab keeps its own flag rather than riding
 * {@link isUpdatedVirtualCardsTableEnabled}: the two surfaces are the same
 * ticket but not the same risk, and this one can be rolled back without taking
 * the virtual-card table with it.
 *
 * Client flag OR `VIEW_PHYSICAL_CARD_MANAGEMENT`, keyed on the VIEW action like
 * every sibling gate on this page.
 *
 * TIMING: the capability arrives asynchronously and fails closed, so an enrolled
 * org with the flag off renders the legacy treatment until the capabilities
 * query settles, then swaps. Cheaper here than anywhere else on the page — the
 * rows do not change, only their chrome.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the redesigned transaction-table treatment should render
 */
export const isUpdatedTransactionsTableEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_transactions_table === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Check if the redesigned card-detail Overview tab is enabled. EX-40632/40633.
 *
 * Differs from the Statements and Activity gates in what it controls: those add
 * a tab, so flag-off renders NotFound. This one swaps the CONTENT of a tab that
 * already exists, so flag-off renders the legacy `AccountOverview` untouched.
 * It never removes Overview from the Subnav, and a direct URL resolves either
 * way.
 *
 * One exception to "content only": physical-only cards have no Overview tab on
 * the legacy path, because the legacy body summarised virtual cards and budgets
 * and redirected them away. The redesigned surface is card-level, so they GAIN
 * the tab when this is on. That is the single way this gate touches the tab set,
 * and it is why the off path keeps the legacy redirect.
 *
 * Client flag OR `VIEW_PHYSICAL_CARD_MANAGEMENT`, the server's demo-enrollment
 * verdict — the same shape as {@link isUpdatedCardSummaryEnabled},
 * {@link isUpdatedPhysicalCardSettingsEnabled} and
 * {@link isUpdatedBudgetsTableEnabled}.
 *
 * This was deliberately flag-ONLY until EX-40737, on the reasoning that
 * `CardManagementAction` had no member meaning "may view the redesigned
 * Overview". That reasoning is stale: `VIEW_PHYSICAL_CARD_MANAGEMENT` is
 * precisely that verdict — it is what the API already uses to decide whether a
 * card is in the demo at all — and it is what every sibling surface on this page
 * now keys on. Leaving Overview out meant an enrolled org got the redesigned
 * hero, settings, statements, activity and budget tables while Overview alone
 * stayed legacy.
 *
 * Keyed on the VIEW action alone and never on another action's verdict: the
 * server decides enrollment and per-action servability separately, so an
 * enrolled card can have its limit actions UNAVAILABLE while still being
 * entitled to the surface.
 *
 * TIMING: capabilities arrive asynchronously and fail closed, so an enrolled org
 * with the flag off renders legacy Overview until the query settles, then swaps.
 * For a PHYSICAL-ONLY card that swap also adds the Overview tab to the Subnav —
 * visible, but safe, because the gate only ever ADDS a tab here and can never
 * unmount a route the user is already sitting on. The flag remains the way to
 * get a flicker-free rollout.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param capabilities - Card Management capabilities for the card in view;
 *   undefined while the query is pending, errored, or disabled, which degrades
 *   the gate to the flag alone
 * @returns true if the redesigned Overview content should render
 */
export const isUpdatedCardOverviewEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  capabilities?: CapabilityDecisionResponse[],
): boolean =>
  remoteConfig?.show_updated_card_overview === true ||
  isCardManagementViewAvailable(capabilities)

/**
 * Check if Suggestions and Predictions feature is enabled for the user.
 * Requires all of:
 * 1. The global show_suggestions_and_predictions kill switch is enabled, AND
 * 2. The user has an active ERP integration.
 *
 * Non-SaaS issuer exclusion is handled upstream by the backend
 * AI_EXPENSE_CATEGORIES entitlement; this util only enforces the kill switch
 * and ERP-connected requirement.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID
 * @param isErpConnected - Whether the org has an active ERP integration
 * @returns true if Suggestions and Predictions is enabled for this user
 */
export const isSuggestionsAndPredictionsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId: string | undefined,
  isErpConnected: boolean,
): boolean => {
  if (!remoteConfig || !organizationId) {
    return false
  }

  return remoteConfig.show_suggestions_and_predictions && isErpConnected
}

/**
 * Check if the ERP disconnect AI-loss warning modal is enabled.
 * EX-39491 — when on, the final disconnect confirmation shows a scary
 * AI-aware warning and requires the admin to type "DISCONNECT".
 */
export const isErpDisconnectAiWarningEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return remoteConfig.show_erp_disconnect_ai_warning
}

/**
 * Whole-feature Bill Pay gate (the new ACH/wire/check bank-account rails that
 * replace the legacy virtual-card bill pay — intentionally NOT gated by the
 * legacy show_bill_pay_* flags). Returns true if either:
 * 1. The global `bill_pay` flag is enabled, OR
 * 2. The org is on `bill_pay_allowlist` — the ONE milestone-agnostic Bill Pay
 *    beta list (an org on it is "in the Bill Pay beta", full stop).
 *
 * This gate alone shows nothing: each milestone surface ANDs it with its own
 * `bill_pay_mX_*` readiness bool (isBillPayM1BankAccountsEnabled,
 * isBillPayM2VendorsEnabled). Beta orgs receive each milestone the moment its
 * bool flips, with no per-milestone allowlists to maintain.
 *
 * When no organizationId is supplied (defensive / future caller) only the global
 * flag is checked. Never throws: a missing allowlist falls back to an empty list.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if this user's org passes the whole-feature Bill Pay gate
 */
export const isBillPayEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  const { bill_pay, bill_pay_allowlist } = remoteConfig

  // If org ID is available, also honor the per-org allowlist
  if (organizationId) {
    return bill_pay || (bill_pay_allowlist ?? []).includes(organizationId)
  }

  // No org ID (defensive / future caller): only use the global flag
  return bill_pay
}

/**
 * Check if Bill Pay M1 (Bank Accounts — the org funding-account experience) is
 * enabled for the user: (whole-feature gate) AND (`bill_pay_m1_bank_accounts`
 * "M1 is ready" bool). The bool keeps milestones releasable independently.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if Bill Pay Bank Accounts is enabled for this user
 */
export const isBillPayM1BankAccountsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return (
    isBillPayEnabled(remoteConfig, organizationId) &&
    remoteConfig.bill_pay_m1_bank_accounts
  )
}

/**
 * Check if Bill Pay M2 (Vendors) is enabled for the user: (whole-feature gate)
 * AND (`bill_pay_m2_vendors` "M2 is ready" bool).
 *
 * The AND keeps milestones releasable independently (M1 can GA while M2 is
 * dark; while M2 is mock-backed/in-build the bool stays off so beta orgs don't
 * see it) and encodes the product dependency: Vendors cannot exist without the
 * Bill Pay bank-account experience.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if Bill Pay Vendors is enabled for this user
 */
export const isBillPayM2VendorsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return (
    isBillPayEnabled(remoteConfig, organizationId) &&
    remoteConfig.bill_pay_m2_vendors
  )
}

/**
 * Check if vendor EDITING (EX-40771/40772) is enabled:
 * (the M2 Vendors gate) AND (`bill_pay_vendor_edit` bool).
 *
 * The bool is scoped to editing alone — it is deliberately NOT a milestone-wide
 * or product-wide switch. Adding a vendor, resuming ACH setup, archiving
 * (EX-40761) and the payments filter (EX-40762) each keep their own gate, so
 * this one can flip without dragging any of them along.
 *
 * Nested on M2 rather than on the whole-feature gate because there is nothing to
 * edit without the Vendors page — the same dependency shape M2 has on M1.
 *
 * Off is the pre-EX-40771 table: only Action Required rows are clickable (to
 * resume ACH setup), every other row is inert, and no edit flyout exists. That
 * matters
 * beyond a normal dark launch — `bill_pay_m2_vendors` is already ON, so without
 * this second bool the edit flyout would reach every beta org the moment it
 * merged, and every save would 400 until extend-api EX-40769's
 * `PATCH /payments/vendors/{id}` details operation is deployed.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if vendor editing is enabled for this user
 */
export const isBillPayVendorEditEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return (
    isBillPayM2VendorsEnabled(remoteConfig, organizationId) &&
    remoteConfig.bill_pay_vendor_edit
  )
}

/**
 * Check if Bill Pay M3 (Payments — the pay-a-vendor experience) is enabled for
 * the user: (whole-feature gate) AND (`bill_pay_m3_payments` "M3 is ready"
 * bool).
 *
 * Same shape as M1/M2 so milestones stay independently releasable.
 *
 * NOT enforced here: a payment needs a funding bank account (M1) and a payable
 * vendor (M2), but this gate does not check either bool — turning M3 on for an
 * org without M1/M2 yields a reachable page with nothing to pay from or to.
 * Ordering the rollout is a release-time decision, deliberately not encoded, so
 * the three milestone gates stay independent and individually reversible.
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationId - The user's organization ID (optional; enables the allowlist path)
 * @returns true if Bill Pay Payments is enabled for this user
 */
export const isBillPayM3PaymentsEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationId?: string,
): boolean => {
  if (!remoteConfig) {
    return false
  }

  return (
    isBillPayEnabled(remoteConfig, organizationId) &&
    remoteConfig.bill_pay_m3_payments
  )
}

/**
 * Check if the physical-card issuance entries are enabled. EX-40738.
 *
 * Gates the two Create New menu entries — "Issue a physical card" and "Request a physical card".
 * Both currently open a placeholder modal: no issuance or request API exists yet, so this governs
 * discoverability only and grants no capability.
 *
 * Client flag OR the server's ORG-SCOPED enrollment verdict, the same flag-OR-server shape as
 * {@link isUpdatedCardSummaryEnabled} — but keyed on a different signal, and deliberately so.
 *
 * WHY NOT `VIEW_PHYSICAL_CARD_MANAGEMENT` LIKE ITS SIBLINGS: that verdict comes from
 * `/creditcards/{creditCardId}/cardmanagement/capabilities`, which is CARD-scoped. The Create New
 * menu is global — there is no card in view to resolve it for. `physicalCardManagementEnabled` on
 * `/organizations/{id}/featureflags` answers the org-scoped question instead, and the menu already
 * reads that endpoint, so consulting it costs no additional request.
 *
 * The two are NOT interchangeable. The org field ignores the per-card allowlist, so an org that is
 * dark but owns individually-enrolled cards reports false here while those cards still resolve
 * `VIEW_PHYSICAL_CARD_MANAGEMENT` = AVAILABLE. That is intended: an org-wide create action should
 * follow org enrollment, not one card's.
 *
 * Note the server has no capability for these actions at all yet — `ISSUE_PHYSICAL_CARD` and
 * `REQUEST_PHYSICAL_CARD_ISSUE` exist in the backend's `CardManagementAction` enum but are absent
 * from `CardManagementCapabilityCatalog.actions`, so `/capabilities` never emits them and gating on
 * either would fail closed forever. When they are added to the catalog, a card-scoped surface
 * should read them directly rather than widening this gate.
 *
 * This gate governs VISIBILITY ONLY. The role split is separate and enforced at the call site: only
 * Admins/Owners see "Issue"; anyone else who can see the menu gets "Request", which routes to an
 * Admin/Owner (the Card Approver).
 *
 * @param remoteConfig - The remote config model from the feature flagger
 * @param organizationCardManagementEnabled - `physicalCardManagementEnabled` from the org feature
 *   flags; undefined while that query is pending, errored, or disabled, which degrades the gate to
 *   the flag alone rather than granting
 * @returns true if the physical-card issuance entries should render
 */
export const isPhysicalCardIssuanceEnabled = (
  remoteConfig: RemoteConfigModel | undefined,
  organizationCardManagementEnabled?: boolean,
): boolean =>
  remoteConfig?.show_physical_card_issuance === true ||
  organizationCardManagementEnabled === true
